import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import * as microsoftTeams from '@microsoft/teams-js'

interface TeamsContextType {
  isInTeams: boolean
  isInitialized: boolean
  teamsContext: microsoftTeams.app.Context | null
  theme: string
  error: string | null
}

const TeamsContext = createContext<TeamsContextType>({
  isInTeams: false,
  isInitialized: false,
  teamsContext: null,
  theme: 'default',
  error: null,
})

export const useTeams = () => useContext(TeamsContext)

interface TeamsProviderProps {
  children: ReactNode
}

export const TeamsProvider = ({ children }: TeamsProviderProps) => {
  const [isInTeams, setIsInTeams] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [teamsContext, setTeamsContext] = useState<microsoftTeams.app.Context | null>(null)
  const [theme, setTheme] = useState('default')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const initializeTeams = async () => {
      try {
        // Vérifier si nous sommes dans un contexte Teams
        // Teams SDK lance une erreur si on n'est pas dans Teams
        await microsoftTeams.app.initialize()
        
        // Si l'initialisation réussit, nous sommes dans Teams
        setIsInTeams(true)
        
        // Récupérer le contexte Teams
        const context = await microsoftTeams.app.getContext()
        setTeamsContext(context)
        
        // Récupérer et appliquer le thème Teams
        if (context.app.theme) {
          setTheme(context.app.theme)
          applyTeamsTheme(context.app.theme)
        }
        
        // S'abonner aux changements de thème
        microsoftTeams.app.registerOnThemeChangeHandler((newTheme) => {
          setTheme(newTheme)
          applyTeamsTheme(newTheme)
        })
        
        // Notifier Teams que l'app est prête
        microsoftTeams.app.notifySuccess()
        
        console.log('Teams SDK initialized successfully')
      } catch (err) {
        // Si l'initialisation échoue, nous ne sommes probablement pas dans Teams
        console.log('Not running in Teams context, continuing in standalone mode')
        setIsInTeams(false)
        setError(err instanceof Error ? err.message : 'Failed to initialize Teams SDK')
      } finally {
        setIsInitialized(true)
      }
    }

    initializeTeams()
  }, [])

  const applyTeamsTheme = (themeName: string) => {
    // Appliquer le thème Teams à l'application
    const root = document.documentElement
    
    switch (themeName) {
      case 'dark':
        root.classList.add('dark')
        root.classList.remove('light')
        break
      case 'contrast':
        root.classList.add('dark', 'high-contrast')
        break
      case 'default':
      default:
        root.classList.remove('dark', 'high-contrast')
        root.classList.add('light')
        break
    }
  }

  return (
    <TeamsContext.Provider
      value={{
        isInTeams,
        isInitialized,
        teamsContext,
        theme,
        error,
      }}
    >
      {children}
    </TeamsContext.Provider>
  )
}

export default TeamsContext
